import React, { useRef, useState } from "react";
import type { LanguagesView } from "../api.js";
import { isInProgress } from "../languages/languages.js";
import { feedEmailsYou } from "../lib/eta.js";
import { useJobActivity } from "../lib/jobActivity.js";
import { LanguageProgress } from "./LanguageProgress.js";
import { switchOptions } from "./languageView.js";

/**
 * Which language the editor shows: English, then each language that can be
 * looked at. A language being made is a tab with a spinner and its percent,
 * and opens its progress. A script waiting for a check carries a dot; one the
 * English has outgrown, a warning dot.
 */
export const LanguageSwitch: React.FC<{
  view: LanguagesView | null;
  /** The shown language's code; null is the English. */
  current: string | null;
  onSwitch: (code: string | null) => void;
  /** The signed-in email, for the "we'll email you" line in a language's progress. */
  email?: string | null;
  /** Which edge of the switch a language's progress hangs from; "right" for a switch at the right of a header. */
  align?: "left" | "right";
}> = ({ view, current, onSwitch, email, align = "left" }) => {
  const wrapper = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<string | null>(null);
  // The feed knows whether a language's job will email the person looking.
  const { active: jobs } = useJobActivity();
  const languages = switchOptions(view, current);
  if (!view || languages.length === 0) return null;

  const segment = (active: boolean) =>
    `inline-flex h-full items-center gap-1.5 px-3 text-[13px] font-medium transition-colors ${
      active ? "bg-ink-100 text-white" : "text-ink-300 hover:bg-bay-750 hover:text-ink-100"
    }`;

  const openLanguage = open ? languages.find((one) => one.code === open && isInProgress(one.status)) : undefined;

  return (
    <div ref={wrapper} className="relative inline-flex">
      <div
        role="group"
        aria-label="Language"
        className="inline-flex h-[34px] overflow-hidden rounded-[8px] border border-[var(--edge-strong)]"
      >
        <button
          type="button"
          className={segment(current === null)}
          aria-pressed={current === null}
          onClick={() => onSwitch(null)}
        >
          {view.source.toUpperCase()}
        </button>
        {languages.map((language) => {
          const active = current === language.code;
          const busy = isInProgress(language.status);
          if (busy) {
            const percent = Math.round((language.progress ?? 0) * 100);
            return (
              <button
                key={language.code}
                type="button"
                className={`${segment(false)} bg-bay-750 text-ember-400`}
                aria-expanded={open === language.code}
                aria-haspopup="dialog"
                title={`${language.nativeName} · being made`}
                onClick={() => setOpen(open === language.code ? null : language.code)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  aria-hidden
                  className="animate-spin"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                >
                  <path d="M12 3a9 9 0 1 0 9 9" />
                </svg>
                {language.code.toUpperCase()}
                <span className="tnum font-mono text-[12px] font-normal">{percent}%</span>
              </button>
            );
          }
          return (
            <button
              key={language.code}
              type="button"
              className={segment(active)}
              aria-pressed={active}
              title={language.outOfDate ? `${language.nativeName} · out of date` : language.nativeName}
              onClick={() => onSwitch(language.code)}
            >
              {language.code.toUpperCase()}
              {/* Out of date wins over review: an out-of-date script cannot be voiced, it has to be updated first. */}
              {language.outOfDate ? (
                <span role="img" aria-label="out of date" className="h-[7px] w-[7px] rounded-full bg-signal-bad" />
              ) : language.status === "review" ? (
                <span role="img" aria-label="needs checking" className="h-[7px] w-[7px] rounded-full bg-ember-400" />
              ) : null}
            </button>
          );
        })}
      </div>
      {openLanguage ? (
        <LanguageProgress
          language={openLanguage}
          email={email ?? null}
          emailsYou={feedEmailsYou(jobs, openLanguage.jobId)}
          free={view.freeForever === true}
          boundary={wrapper}
          align={align}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
};
